import { z } from 'zod';
import { SCENE_REFERENCE_LIMIT } from './scene-references.js';
export const SceneReferencePackingSchema = z.object({
    groups: z
        .array(z.array(z.string().min(1)).min(1))
        .min(1)
        .max(SCENE_REFERENCE_LIMIT),
    characterAssetIds: z.array(z.string().min(1)),
});
/** Only inanimate references may share an image. Keep people and animals independent. */
export function packSceneReferences(input) {
    const byId = new Map();
    for (const reference of input) {
        const previous = byId.get(reference.assetId);
        if (!previous)
            byId.set(reference.assetId, reference);
        else if (reference.category === 'character' && previous.category !== 'character')
            byId.set(reference.assetId, { ...reference, name: `${reference.name} / ${previous.name}` });
    }
    const references = [...byId.values()];
    const characterAssetIds = references
        .filter((r) => r.category === 'character')
        .map((r) => r.assetId);
    const others = references.filter((r) => r.category !== 'character');
    if (characterAssetIds.length + (others.length ? 1 : 0) > SCENE_REFERENCE_LIMIT)
        throw new Error('The character references leave no room for the other subjects');
    let groups = references.map((r) => [r.assetId]);
    if (groups.length > SCENE_REFERENCE_LIMIT) {
        const recurring = references.filter((r) => r.category === 'recurring').map((r) => r.assetId);
        const available = Math.max(1, SCENE_REFERENCE_LIMIT - (references.length - recurring.length));
        groups = combine(references, recurring, available);
    }
    if (groups.length > SCENE_REFERENCE_LIMIT) {
        const slots = SCENE_REFERENCE_LIMIT - characterAssetIds.length;
        const recurring = others.filter((r) => r.category === 'recurring').map((r) => r.assetId);
        const remaining = others.filter((r) => r.category !== 'recurring').map((r) => r.assetId);
        // Preserve the first-priority board unless every inanimate subject must share one slot.
        groups =
            recurring.length > 1 && remaining.length > 0 && slots > 1
                ? arrange(references, [recurring, ...chunks(remaining, slots - 1)])
                : combine(references, others.map((r) => r.assetId), slots);
    }
    return { references, packing: { groups, characterAssetIds } };
}
/** Balance boards over the available slots so individual details remain as large as possible. */
function combine(references, candidates, slots) {
    return arrange(references, chunks(candidates, slots));
}
function chunks(candidates, slots) {
    if (candidates.length <= slots)
        return candidates.map((id) => [id]);
    const groups = [];
    let offset = 0;
    for (let left = slots; left > 0; left--) {
        const size = Math.ceil((candidates.length - offset) / left);
        groups.push(candidates.slice(offset, offset + size));
        offset += size;
    }
    return groups;
}
function arrange(references, groups) {
    const owner = new Map(groups.flatMap((group) => group.map((id) => [id, group])));
    return references.flatMap((reference) => {
        const group = owner.get(reference.assetId);
        return !group ? [[reference.assetId]] : group[0] === reference.assetId ? [group] : [];
    });
}
/** Reject malformed/replayed jobs before fetching files or uploading anything to a provider. */
export function validateSceneReferencePacking(value, assetIds) {
    const result = SceneReferencePackingSchema.safeParse(value);
    if (!result.success)
        return false;
    const { groups, characterAssetIds } = result.data;
    const flat = groups.flat();
    const expected = new Set(assetIds);
    return (expected.size === assetIds.length &&
        flat.length === expected.size &&
        new Set(flat).size === flat.length &&
        flat.every((id) => expected.has(id)) &&
        new Set(characterAssetIds).size === characterAssetIds.length &&
        characterAssetIds.every((id) => expected.has(id) && groups.some((group) => group.length === 1 && group[0] === id)));
}
export function referencePackingPrompt(references, packing) {
    const names = new Map(references.map((r) => [r.assetId, r.name]));
    const descriptions = packing.groups.map((group, index) => group.length === 1
        ? `Reference image ${index + 1}: ${names.get(group[0])}`
        : `Reference image ${index + 1} is a reference board: ${group.map((id, cell) => `cell ${cell + 1} = ${names.get(id)}`).join('; ')}`);
    if (packing.groups.some((group) => group.length > 1))
        descriptions.push('Board cells are numbered left to right, top to bottom. Each cell is a separate subject reference, not the scene composition. Preserve every referenced design. Do not draw the board, grid, cell numbers, borders or reference backgrounds in the illustration. Characters have separate references showing their assigned outfits; do not exchange their faces or clothes');
    return descriptions.join('. ') + (descriptions.length ? '.' : '');
}
//# sourceMappingURL=reference-packing.js.map